import { useMemo, useState } from "react"

import type { SegmentClaimEvidence, SegmentClaimRecord, SegmentClaimRegistry } from "../types"

import { ChevronDown, FileText, ShieldCheck } from "@/components/icons"
import { cn } from "@/lib/utils"

import { SourceLinks } from "./segment-detail-primitives"
import {
  asDisplayString,
  claimFieldPathLabel,
  claimFreshnessLabel,
  claimOriginLabel,
} from "../utils/claim-display"
import { formatStructuredClaimText } from "../utils/claim-text-utils"

type ClaimsEvidenceViewProps = {
  registry?: SegmentClaimRegistry | null
  selectedClaimId?: string | null
  onSelectedClaimIdChange?: (claimId: string | null) => void
}

function verificationLabel(status: unknown): string {
  const raw = asDisplayString(status, "not_checked")
  if (raw === "manual_assertion") return "Manual assertion"
  return raw.replace(/_/g, " ")
}

function verificationTone(status: unknown): string {
  const raw = asDisplayString(status, "not_checked")
  if (raw === "verified") return "bg-success/10 text-success"
  if (raw === "contradicted") return "bg-destructive/10 text-destructive"
  if (raw === "manual_assertion") return "bg-warning/15 text-warning-foreground"
  return "bg-warning/15 text-warning-foreground"
}

function evidenceTitle(evidence: SegmentClaimEvidence): string {
  return (
    asDisplayString(evidence.title) ||
    asDisplayString(evidence.url) ||
    asDisplayString(evidence.file) ||
    asDisplayString(evidence.sourceId) ||
    "Source"
  )
}

/** Renders one evidence item's own date stamps + quote (there can be several — §26c). */
function EvidenceItemDetails({ evidence }: { evidence: SegmentClaimEvidence }) {
  const hasDates = Boolean(evidence.sourceDate || evidence.retrievedAt)
  const quote = evidence.quote || evidence.snippet
  if (!hasDates && !quote) return null

  return (
    <div className="border-border/60 mt-2 border-t pt-2 first:mt-0 first:border-t-0 first:pt-0">
      {hasDates ? (
        <dl className="grid gap-1 text-xs sm:grid-cols-2">
          {evidence.sourceDate ? (
            <div>
              <dt className="text-muted-foreground">Source date</dt>
              <dd className="text-foreground">{asDisplayString(evidence.sourceDate)}</dd>
            </div>
          ) : null}
          {evidence.retrievedAt ? (
            <div>
              <dt className="text-muted-foreground">Retrieved</dt>
              <dd className="text-foreground">{asDisplayString(evidence.retrievedAt)}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {quote ? (
        <blockquote className="border-primary/40 text-foreground mt-2 border-l-2 pl-3 text-sm leading-6">
          {formatStructuredClaimText(quote) ?? asDisplayString(quote)}
        </blockquote>
      ) : null}
    </div>
  )
}

function EvidencePanel({ claim }: { claim: SegmentClaimRecord }) {
  const freshness = claimFreshnessLabel(claim.freshness)
  const verificationStatus = asDisplayString(claim.verification.status, "not_checked")
  const verificationNote = asDisplayString(claim.verification.note)
  // Every evidence item, not only evidence[0] (§26c) — a claim can be backed
  // by more than one source.
  const sourcedEvidence = claim.evidence.filter((item) => item.url || item.file)
  const sourceUrls = sourcedEvidence
    .map((item) => item.url)
    .filter((url): url is string => Boolean(url))
  const fileOnlyEvidence = sourcedEvidence.filter((item) => !item.url && item.file)
  const detailedEvidence = claim.evidence.filter(
    (item) => item.sourceDate || item.retrievedAt || item.quote || item.snippet
  )
  const hasExternalSource = sourcedEvidence.length > 0

  return (
    <aside aria-live="polite" className="border-border bg-muted/35 rounded-lg border p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="bg-primary/10 text-primary inline-flex size-7 items-center justify-center rounded-md">
          <FileText className="size-3.5" />
        </span>
        <div>
          <p className="text-foreground text-sm font-semibold">
            {/* Plainly say there's no external source rather than showing a
                generic "Source" header for evidence with no url/file (§26d):
                that implied an attachment that was actually missing. */}
            {hasExternalSource ? evidenceTitle(sourcedEvidence[0]) : "No external source attached"}
          </p>
          <p className="text-muted-foreground text-xs">Freshness: {freshness}</p>
        </div>
      </div>
      {/* Clickable, new-tab sources via the shared SourceLinks primitive (§26c). */}
      {sourceUrls.length > 0 ? <SourceLinks sources={sourceUrls} /> : null}
      {fileOnlyEvidence.length > 0 ? (
        <ul className="text-muted-foreground mt-2 grid gap-1 text-xs">
          {fileOnlyEvidence.map((item, index) => (
            <li key={item.sourceId ?? item.file ?? index}>{asDisplayString(item.file)}</li>
          ))}
        </ul>
      ) : null}
      {detailedEvidence.map((item, index) => (
        <EvidenceItemDetails
          key={item.sourceId ?? item.url ?? item.file ?? index}
          evidence={item}
        />
      ))}
      <p className="text-muted-foreground mt-2 text-xs">Verification: {verificationStatus}</p>
      {verificationNote ? (
        <p
          className={cn(
            "mt-2 rounded-md px-2 py-1.5 text-xs leading-5",
            verificationStatus === "contradicted"
              ? "bg-destructive/10 text-destructive"
              : "bg-warning/15 text-warning-foreground"
          )}
        >
          Why: {verificationNote}
        </p>
      ) : null}
    </aside>
  )
}

function ClaimRow({
  claim,
  selected,
  onSelect,
}: {
  claim: SegmentClaimRecord
  selected: boolean
  onSelect: () => void
}) {
  const hasEvidence = claim.evidence.length > 0
  // Some claim text leaks as a JSON-encoded object rather than prose (§26b);
  // format it into a readable phrase before falling back to the raw string.
  const claimText =
    formatStructuredClaimText(claim.text) ?? asDisplayString(claim.text, "Untitled claim")
  const freshness = claimFreshnessLabel(claim.freshness)
  const fieldLabel = claimFieldPathLabel(claim.fieldPath)

  return (
    <li className="border-border bg-card rounded-lg border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-sm leading-6">{claimText}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase">
              {claimOriginLabel(claim.origin)}
            </span>
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                verificationTone(claim.verification.status)
              )}
            >
              {verificationLabel(claim.verification.status)}
            </span>
            <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase">
              {freshness}
            </span>
            <span className="text-muted-foreground text-xs">{fieldLabel}</span>
          </div>
        </div>
        <button
          aria-label={`Open evidence for ${fieldLabel}`}
          className="btn-spring border-input bg-background text-foreground hover:bg-accent inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition"
          type="button"
          onClick={onSelect}
        >
          {hasEvidence ? <ShieldCheck className="size-3.5" /> : <FileText className="size-3.5" />}
          {hasEvidence
            ? `${claim.evidence.length} source${claim.evidence.length === 1 ? "" : "s"}`
            : "Source"}
        </button>
      </div>
      {selected ? (
        <div className="mt-3">
          <EvidencePanel claim={claim} />
        </div>
      ) : null}
    </li>
  )
}

export function ClaimsEvidenceView({
  registry,
  selectedClaimId,
  onSelectedClaimIdChange,
}: ClaimsEvidenceViewProps) {
  const claims = useMemo(
    () => (registry?.claims ?? []).filter((claim) => claim.status === "approved"),
    [registry]
  )
  const [internalSelectedClaimId, setInternalSelectedClaimId] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const isControlled = onSelectedClaimIdChange !== undefined
  const currentSelectedClaimId = isControlled ? (selectedClaimId ?? null) : internalSelectedClaimId
  const setSelectedClaimId = onSelectedClaimIdChange ?? setInternalSelectedClaimId
  const visible = isExpanded || currentSelectedClaimId !== null
  // An explicit close always wins (§26a): collapsing must also clear any
  // selected claim, or `visible` stays true via `currentSelectedClaimId !==
  // null` and "Review claims" can never close the panel again.
  const toggleVisible = () => {
    if (visible) {
      setIsExpanded(false)
      setSelectedClaimId(null)
    } else {
      setIsExpanded(true)
    }
  }
  const verifiedCount = claims.filter(
    (claim) => asDisplayString(claim.verification.status) === "verified"
  ).length
  const manualCount = claims.filter(
    (claim) => asDisplayString(claim.verification.status) === "manual_assertion"
  ).length

  if (claims.length === 0) {
    return null
  }

  return (
    <section className="border-border bg-card rounded-lg border p-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="bg-primary/10 text-primary inline-flex size-8 shrink-0 items-center justify-center rounded-md">
            <ShieldCheck className="size-4" />
          </span>
          <div className="min-w-0">
            <h3 className="text-foreground text-sm font-semibold">Claims & evidence</h3>
            <p className="text-muted-foreground truncate text-xs">
              {claims.length} approved claim{claims.length === 1 ? "" : "s"} · {verifiedCount}{" "}
              verified
              {manualCount ? ` · ${manualCount} manual` : ""}
            </p>
          </div>
        </div>
        <button
          aria-expanded={visible}
          className="btn-spring border-input bg-background text-foreground hover:bg-accent inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition"
          type="button"
          onClick={toggleVisible}
        >
          Review claims
          <ChevronDown className={cn("size-3.5 transition", visible ? "rotate-180" : "")} />
        </button>
      </div>
      {visible ? (
        <ul className="mt-3 grid gap-3">
          {claims.map((claim) => (
            <ClaimRow
              key={claim.claimId}
              claim={claim}
              selected={currentSelectedClaimId === claim.claimId}
              onSelect={() =>
                setSelectedClaimId(currentSelectedClaimId === claim.claimId ? null : claim.claimId)
              }
            />
          ))}
        </ul>
      ) : null}
    </section>
  )
}
